import { OrganizerApiError, type OrganizerTokenSource, type RemarketingAccessResponse } from './api';
import { HandshakeError, type Handshake } from './handshake';
import type { ParentOriginsConfig } from './parentOrigins';

// Sessão da área do organizador: a credencial temporária que chega do Gestão.
//
// O token vive SÓ nesta closure — nem estado React, nem storage, nem URL, nem
// cookie. Ele é liberado para os dados apenas depois de o `/api/organizer/access`
// confirmar quem é e de qual organização; renova por novo handshake perto do
// fim (e reconfere o acesso); e, se a identidade ou a organização mudar, abre
// uma ÉPOCA nova — a tela descarta tudo o que era da anterior.
//
// Limite explícito: sair do Gestão ou desligar a flag impede novas emissões,
// mas não revoga o token já emitido (até 5 min). O backend do Remarketing
// continua conferindo usuário e permissões a cada consulta.

/** Renova quando falta ~1 min para o fim do token. */
const RENEW_MARGIN_MS = 60_000;
/** Sem `init` válido nesse prazo, a página não foi aberta pelo Gestão. */
const PARENT_WAIT_MS = 12_000;
const CONTACT_OPERATIONS = ['journeys:read', 'leads:read'];

export interface OrganizerEvent {
  id: string;
  title: string | null;
}

export interface OrganizerAccess {
  userId: string;
  role: string;
  organization: {
    id: string;
    name: string | null;
    operations: readonly string[];
    events: readonly OrganizerEvent[];
    /** Há mais eventos autorizados do que os listados: a consulta exige evento. */
    eventsTruncated: boolean;
  };
}

export type OrganizerForbiddenReason =
  | 'gestao_denied'
  | 'no_contacts_permission'
  | 'not_an_organizer'
  | 'organization_not_authorized'
  | 'permission_denied'
  | 'forbidden';

export type OrganizerSessionState =
  | { status: 'misconfigured' }
  | { status: 'not_embedded' }
  | { status: 'connecting' }
  | { status: 'ready'; access: OrganizerAccess; epoch: number }
  | { status: 'forbidden'; reason: OrganizerForbiddenReason }
  | { status: 'session_ended' }
  | { status: 'unavailable' };

export class OrganizerSessionError extends Error {
  readonly status: OrganizerSessionState['status'];
  constructor(status: OrganizerSessionState['status']) {
    super(
      status === 'session_ended'
        ? 'Sua sessão terminou. Recarregue a página do Gestão para continuar.'
        : 'Sessão do organizador indisponível.',
    );
    this.name = 'OrganizerSessionError';
    this.status = status;
  }
}

interface VisibilitySource {
  readonly visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface OrganizerSessionDeps {
  parentOrigins: ParentOriginsConfig;
  /** `false` quando a página foi aberta fora de um iframe. */
  embedded: boolean;
  createHandshake(onParentReady: () => void): Handshake;
  fetchAccess(token: string, signal: AbortSignal): Promise<RemarketingAccessResponse>;
  visibility?: VisibilitySource;
  now?: () => number;
}

export interface OrganizerSession extends OrganizerTokenSource {
  getState(): OrganizerSessionState;
  subscribe(listener: () => void): () => void;
  start(): void;
  retry(): void;
  dispose(): void;
}

const FORBIDDEN_CODES: ReadonlySet<string> = new Set([
  'not_an_organizer',
  'organization_not_authorized',
  'permission_denied',
]);

function isText(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** Só o contrato de `/v1/me/remarketing-access`; qualquer desvio é indisponibilidade. */
export function parseAccess(body: RemarketingAccessResponse): OrganizerAccess | null {
  const organization = body?.organizations?.[0];
  if (!isText(body?.user?.id) || !organization || !isText(organization.id)) return null;
  if (!Array.isArray(organization.operations) || !Array.isArray(organization.events)) return null;
  if (typeof organization.eventsTruncated !== 'boolean') return null;

  const events: OrganizerEvent[] = [];
  for (const event of organization.events) {
    if (!isText(event?.id)) return null;
    events.push({ id: event.id, title: typeof event.title === 'string' ? event.title : null });
  }

  return {
    userId: body.user.id,
    role: String(body.user.role ?? ''),
    organization: {
      id: organization.id,
      name: typeof organization.name === 'string' ? organization.name : null,
      operations: organization.operations.filter((operation): operation is string => typeof operation === 'string'),
      events,
      eventsTruncated: organization.eventsTruncated,
    },
  };
}

export function createOrganizerSession(deps: OrganizerSessionDeps): OrganizerSession {
  const now = deps.now ?? (() => Date.now());
  const listeners = new Set<() => void>();

  let state: OrganizerSessionState = { status: 'connecting' };
  let token: { value: string; expiresAt: number } | null = null;
  let identity: string | null = null;
  let epoch = 0;
  let handshake: Handshake | null = null;
  let refreshing: Promise<void> | null = null;
  let accessController: AbortController | null = null;
  let parentTimer: ReturnType<typeof setTimeout> | null = null;
  let renewTimer: ReturnType<typeof setTimeout> | null = null;
  let started = false;
  // Estado final desta página (sessão encerrada, sem permissão, fora do Gestão):
  // resultados que chegarem depois são ignorados.
  let closed = false;
  let disposed = false;

  function setState(next: OrganizerSessionState) {
    if (disposed) return;
    state = next;
    for (const listener of [...listeners]) listener();
  }

  function clearCredential() {
    token = null;
    if (renewTimer !== null) clearTimeout(renewTimer);
    renewTimer = null;
  }

  function stopListening() {
    if (parentTimer !== null) clearTimeout(parentTimer);
    parentTimer = null;
    accessController?.abort();
    accessController = null;
    handshake?.dispose();
    deps.visibility?.removeEventListener('visibilitychange', onVisibilityChange);
  }

  /** Sem volta nesta página: sessão encerrada, sem permissão ou fora do Gestão. */
  function terminate(next: OrganizerSessionState) {
    closed = true;
    clearCredential();
    stopListening();
    setState(next);
  }

  function becomeUnavailable() {
    clearCredential();
    setState({ status: 'unavailable' });
  }

  function scheduleRenewal() {
    if (renewTimer !== null) clearTimeout(renewTimer);
    if (!token) return;
    const lifetime = token.expiresAt - now();
    const delay = Math.max(lifetime - RENEW_MARGIN_MS, lifetime / 2, 0);
    renewTimer = setTimeout(() => {
      renewTimer = null;
      void refresh();
    }, delay);
  }

  function needsRenewal(): boolean {
    return !token || now() >= token.expiresAt - RENEW_MARGIN_MS;
  }

  async function authenticate(): Promise<void> {
    if (!handshake) return;

    let grant;
    try {
      grant = await handshake.requestToken();
    } catch (err) {
      if (disposed || closed) return;
      const code = err instanceof HandshakeError ? err.code : 'unavailable';
      if (code === 'session_expired') return terminate({ status: 'session_ended' });
      if (code === 'forbidden') return terminate({ status: 'forbidden', reason: 'gestao_denied' });
      return becomeUnavailable();
    }
    if (disposed || closed) return;
    const candidate = { value: grant.accessToken, expiresAt: now() + grant.expiresIn * 1000 };

    // Confere a identidade ANTES de o token novo servir a qualquer consulta.
    accessController?.abort();
    const controller = new AbortController();
    accessController = controller;
    let access: OrganizerAccess | null;
    try {
      access = parseAccess(await deps.fetchAccess(candidate.value, controller.signal));
    } catch (err) {
      if (disposed || closed || controller.signal.aborted) return;
      if (err instanceof OrganizerApiError && err.status === 401) {
        return terminate({ status: 'session_ended' });
      }
      if (err instanceof OrganizerApiError && err.status === 403) {
        const reason = err.code && FORBIDDEN_CODES.has(err.code) ? (err.code as OrganizerForbiddenReason) : 'forbidden';
        return terminate({ status: 'forbidden', reason });
      }
      return becomeUnavailable();
    } finally {
      if (accessController === controller) accessController = null;
    }
    if (disposed || closed) return;
    if (!access) return becomeUnavailable();
    if (!access.organization.operations.some((operation) => CONTACT_OPERATIONS.includes(operation))) {
      return terminate({ status: 'forbidden', reason: 'no_contacts_permission' });
    }

    token = candidate;
    scheduleRenewal();
    const nextIdentity = `${access.userId}:${access.organization.id}`;
    if (nextIdentity !== identity) {
      identity = nextIdentity;
      epoch += 1;
    }
    setState({ status: 'ready', access, epoch });
  }

  function refresh(): Promise<void> {
    if (!refreshing) {
      refreshing = authenticate()
        // Renovação roda em segundo plano (timer, aba de volta): erro fora do
        // previsto vira indisponibilidade, nunca rejeição solta.
        .catch(() => {
          if (!disposed && !closed) becomeUnavailable();
        })
        .finally(() => {
          refreshing = null;
        });
    }
    return refreshing;
  }

  function onParentReady() {
    if (parentTimer !== null) clearTimeout(parentTimer);
    parentTimer = null;
    void refresh();
  }

  function onVisibilityChange() {
    if (deps.visibility?.visibilityState !== 'visible' || state.status !== 'ready') return;
    // Aba de volta depois do prazo: renova antes de qualquer consulta.
    if (needsRenewal()) void refresh();
  }

  return {
    get epoch() {
      return epoch;
    },

    getState() {
      return state;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    start() {
      if (started || disposed) return;
      started = true;
      if (deps.parentOrigins.status !== 'ok') return setState({ status: 'misconfigured' });
      if (!deps.embedded) return setState({ status: 'not_embedded' });

      setState({ status: 'connecting' });
      handshake = deps.createHandshake(onParentReady);
      deps.visibility?.addEventListener('visibilitychange', onVisibilityChange);
      parentTimer = setTimeout(() => {
        parentTimer = null;
        if (handshake?.parentOrigin === null) terminate({ status: 'not_embedded' });
      }, PARENT_WAIT_MS);
    },

    retry() {
      if (disposed || state.status !== 'unavailable' || !handshake?.parentOrigin) return;
      setState({ status: 'connecting' });
      void refresh();
    },

    async getAccessToken(options) {
      if (disposed || closed) throw new OrganizerSessionError(state.status);
      if (options?.forceRefresh || needsRenewal()) {
        // Renova só com a sessão de pé (ou juntando-se à renovação em curso);
        // antes do init do Gestão não há a quem pedir.
        if (state.status !== 'ready' && !refreshing) throw new OrganizerSessionError(state.status);
        await refresh();
      }
      if (state.status !== 'ready' || !token) throw new OrganizerSessionError(state.status);
      return token.value;
    },

    endSession() {
      if (disposed || closed) return;
      terminate({ status: 'session_ended' });
    },

    dispose() {
      if (disposed) return;
      clearCredential();
      stopListening();
      disposed = true;
      listeners.clear();
    },
  };
}
